/**
 * Mordeczka.eu Admin - Cinemas Page
 * Management page for Cinemas and their Halls
 */

import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Table,
  Button,
  Space,
  Typography,
  Card,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  message,
  List,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ShopOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { cinemasApi, cinemaHallsApi } from "../services/api";
import type {
  Cinema,
  CinemaCreate,
  CinemaUpdate,
  CinemaHall,
  CinemaHallCreate,
  CinemaHallUpdate,
} from "../types";
import { usePermissions } from "../hooks/usePermissions";

const { Title, Text } = Typography;

export function CinemasPage() {
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isHallModalOpen, setIsHallModalOpen] = useState(false);
  const [editingCinema, setEditingCinema] = useState<Cinema | null>(null);
  const [editingHall, setEditingHall] = useState<CinemaHall | null>(null);
  const [currentCinemaId, setCurrentCinemaId] = useState<string | null>(null);
  const [form] = Form.useForm();
  const [hallForm] = Form.useForm();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 100 });
  const { canEdit, canDelete } = usePermissions();

  const { data, isLoading } = useQuery({
    queryKey: ["cinemas", pagination],
    queryFn: () =>
      cinemasApi.list({
        page: pagination.current,
        page_size: pagination.pageSize,
      }),
  });

  const createMutation = useMutation({
    mutationFn: cinemasApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cinemas"] });
      message.success("Kino dodane pomyślnie");
      handleCloseModal();
    },
    onError: () => message.error("Nie udało się dodać kina"),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: CinemaUpdate }) =>
      cinemasApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cinemas"] });
      message.success("Kino zaktualizowane pomyślnie");
      handleCloseModal();
    },
    onError: () => message.error("Nie udało się zaktualizować kina"),
  });

  const deleteMutation = useMutation({
    mutationFn: cinemasApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cinemas"] });
      message.success("Kino usunięte pomyślnie");
    },
    onError: () => message.error("Nie udało się usunąć kina"),
  });

  // Hall mutations
  const createHallMutation = useMutation({
    mutationFn: ({
      cinemaId,
      data,
    }: {
      cinemaId: string;
      data: Omit<CinemaHallCreate, "cinema_id">;
    }) => cinemaHallsApi.create(cinemaId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cinemas"] });
      message.success("Sala dodana pomyślnie");
      handleCloseHallModal();
    },
    onError: () => message.error("Nie udało się dodać sali"),
  });

  const updateHallMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: CinemaHallUpdate }) =>
      cinemaHallsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cinemas"] });
      message.success("Sala zaktualizowana pomyślnie");
      handleCloseHallModal();
    },
    onError: () => message.error("Nie udało się zaktualizować sali"),
  });

  const deleteHallMutation = useMutation({
    mutationFn: cinemaHallsApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cinemas"] });
      message.success("Sala usunięta pomyślnie");
    },
    onError: () => message.error("Nie udało się usunąć sali"),
  });

  const handleOpenModal = (cinema?: Cinema) => {
    setEditingCinema(cinema || null);
    if (cinema) {
      form.setFieldsValue(cinema);
    } else {
      form.resetFields();
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingCinema(null);
    form.resetFields();
  };

  const handleOpenHallModal = (cinemaId: string, hall?: CinemaHall) => {
    setCurrentCinemaId(cinemaId);
    setEditingHall(hall || null);
    if (hall) {
      hallForm.setFieldsValue(hall);
    } else {
      hallForm.resetFields();
    }
    setIsHallModalOpen(true);
  };

  const handleCloseHallModal = () => {
    setIsHallModalOpen(false);
    setEditingHall(null);
    setCurrentCinemaId(null);
    hallForm.resetFields();
  };

  const handleSubmit = (values: CinemaCreate | CinemaUpdate) => {
    if (editingCinema) {
      updateMutation.mutate({ id: editingCinema.id, data: values });
    } else {
      createMutation.mutate(values as CinemaCreate);
    }
  };

  const handleHallSubmit = (values: Omit<CinemaHallCreate, "cinema_id">) => {
    if (editingHall) {
      updateHallMutation.mutate({ id: editingHall.id, data: values });
    } else if (currentCinemaId) {
      createHallMutation.mutate({ cinemaId: currentCinemaId, data: values });
    }
  };

  const columns: ColumnsType<Cinema> = [
    {
      title: "Nazwa",
      dataIndex: "name",
      key: "name",
      render: (text: string) => <span style={{ fontWeight: 600 }}>{text}</span>,
    },
    {
      title: "Miasto",
      dataIndex: "city",
      key: "city",
      render: (text: string | null) => text || "-",
    },
    {
      title: "Adres",
      dataIndex: "address",
      key: "address",
      responsive: ["md"],
      render: (text: string | null) => text || "-",
    },
    {
      title: "Sale",
      key: "halls_count",
      width: 80,
      align: "center",
      render: (_: unknown, record: Cinema) => record.halls?.length || 0,
    },
    {
      title: "Akcje",
      key: "actions",
      width: 120,
      align: "center" as const,
      render: (_: unknown, record: Cinema) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleOpenModal(record)}
          />
          <Popconfirm
            title="Usunąć to kino?"
            description="Tej operacji nie można cofnąć. Wszystkie sale zostaną usunięte."
            onConfirm={() => deleteMutation.mutate(record.id)}
            okText="Usuń"
            cancelText="Anuluj"
            okButtonProps={{ danger: true }}
            disabled={!canDelete}
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              disabled={!canDelete}
              title={!canDelete ? "Brak uprawnień do usuwania" : "Usuń"}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const expandedRowRender = (cinema: Cinema) => (
    <div style={{ padding: "0 16px 16px" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 8,
        }}
      >
        <Text strong>Sale kinowe</Text>
        <Button
          type="dashed"
          icon={<PlusOutlined />}
          size="small"
          onClick={() => handleOpenHallModal(cinema.id)}
        >
          Dodaj salę
        </Button>
      </div>
      {cinema.halls && cinema.halls.length > 0 ? (
        <List
          size="small"
          dataSource={cinema.halls}
          renderItem={(hall) => (
            <List.Item
              actions={[
                <Button
                  key="edit"
                  type="text"
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => handleOpenHallModal(cinema.id, hall)}
                />,
                ...(canDelete
                  ? [
                      <Popconfirm
                        key="delete"
                        title="Usunąć tę salę?"
                        onConfirm={() => deleteHallMutation.mutate(hall.id)}
                        okText="Usuń"
                        cancelText="Anuluj"
                        okButtonProps={{ danger: true }}
                      >
                        <Button
                          type="text"
                          size="small"
                          danger
                          icon={<DeleteOutlined />}
                        />
                      </Popconfirm>,
                    ]
                  : []),
              ]}
            >
              <List.Item.Meta
                title={hall.hall_name}
                description={
                  hall.seat_count ? `${hall.seat_count} miejsc` : undefined
                }
              />
            </List.Item>
          )}
        />
      ) : (
        <Text type="secondary">Brak sal</Text>
      )}
    </div>
  );

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        <Title level={2} style={{ margin: 0 }}>
          <ShopOutlined style={{ marginRight: 12, color: "#fa8c16" }} />
          Kina
        </Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => handleOpenModal()}
        >
          Dodaj kino
        </Button>
      </div>

      <Card>
        <Table
          columns={columns}
          dataSource={data?.items || []}
          rowKey="id"
          loading={isLoading}
          expandable={{
            expandedRowRender,
            rowExpandable: () => true,
          }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: data?.total || 0,
            showSizeChanger: true,
            pageSizeOptions: ["100", "500", "1000"],
            showTotal: (total: number) => `Łącznie ${total} kin`,
          }}
          onChange={(p) =>
            setPagination({
              current: p.current || 1,
              pageSize: p.pageSize || 100,
            })
          }
        />
      </Card>

      {/* Cinema Modal */}
      <Modal
        title={editingCinema ? "Edytuj kino" : "Dodaj kino"}
        open={isModalOpen}
        onCancel={handleCloseModal}
        footer={null}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="name"
            label="Nazwa"
            rules={[{ required: true, message: "Nazwa jest wymagana" }]}
          >
            <Input placeholder="Nazwa kina" />
          </Form.Item>
          <Form.Item name="city" label="Miasto">
            <Input placeholder="Miasto" />
          </Form.Item>
          <Form.Item name="address" label="Adres">
            <Input placeholder="Adres" />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
            <Space>
              <Button onClick={handleCloseModal}>Anuluj</Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={createMutation.isPending || updateMutation.isPending}
                disabled={!canEdit}
                title={!canEdit ? "Brak uprawnień do edycji" : undefined}
              >
                {editingCinema ? "Zapisz" : "Dodaj"}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Hall Modal */}
      <Modal
        title={editingHall ? "Edytuj salę" : "Dodaj salę"}
        open={isHallModalOpen}
        onCancel={handleCloseHallModal}
        footer={null}
        destroyOnClose
      >
        <Form form={hallForm} layout="vertical" onFinish={handleHallSubmit}>
          <Form.Item
            name="hall_name"
            label="Nazwa sali"
            rules={[{ required: true, message: "Nazwa sali jest wymagana" }]}
          >
            <Input placeholder="np. Sala 1, IMAX" />
          </Form.Item>
          <Form.Item name="seat_count" label="Liczba miejsc">
            <InputNumber
              min={1}
              placeholder="Liczba miejsc"
              style={{ width: "100%" }}
            />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
            <Space>
              <Button onClick={handleCloseHallModal}>Anuluj</Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={
                  createHallMutation.isPending || updateHallMutation.isPending
                }
                disabled={!canEdit}
                title={!canEdit ? "Brak uprawnień do edycji" : undefined}
              >
                {editingHall ? "Zapisz" : "Dodaj"}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default CinemasPage;
